import React from "react";
import { Target } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import PremiumEventCard from "../components/ui/PremiumEventCard";
import { DartsEvent } from "../types";
import { useEvents } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import { splitEventsByTimeline } from "../utils/eventSections";

interface DashboardViewProps {
  onEventClick: (event: DartsEvent) => void;
  onNotificationClick: () => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({
  onEventClick,
  onNotificationClick,
}) => {
  const { events, isLoading, error } = useEvents();
  const { mypage } = useMypageContext();

  const { upNext, schedule, pastEvents } = splitEventsByTimeline(events);

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        <PremiumHeader
          title={UI_TEXT.common.appName}
          icon={Target}
          theme="red"
          onNotificationClick={onNotificationClick}
        />
        <div className="px-6 py-6 pb-40 text-left">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="red"
          />
          {isLoading && (
            <p className="text-slate-400 text-xs font-bold text-center py-8">
              {UI_TEXT.common.loading}
            </p>
          )}
          {error && (
            <p className="text-rose-500 text-xs font-bold text-center py-8">
              {error}
            </p>
          )}
          {!isLoading && !error && upNext && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.upNext}
              </h2>
              <PremiumEventCard
                event={upNext}
                isPrimary
                theme="red"
                onClick={onEventClick}
              />
            </>
          )}
          {!isLoading && !error && schedule.length > 0 && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.schedule}
              </h2>
              {schedule.map((ev) => (
                <PremiumEventCard
                  key={ev.id}
                  event={ev}
                  isPrimary={false}
                  theme="red"
                  onClick={onEventClick}
                />
              ))}
            </>
          )}
          {!isLoading && !error && pastEvents.length > 0 && (
            <>
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.pastEvents}
              </h2>
              {pastEvents.map((ev) => (
                <PremiumEventCard
                  key={ev.id}
                  event={ev}
                  isPrimary={false}
                  isPast
                  theme="red"
                  onClick={onEventClick}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
